import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { type DriOwnerUpdate } from '@apps/web/components/admin/accounts/account_card';
import { type AccountHealthVerdictSelection } from '@apps/web/components/admin/accounts/account_health_status';
import { ONBOARDING_WEEKLY_CAP } from '@apps/web/components/admin/accounts/calendar_capacity';
import { type CalendarViewMode } from '@apps/web/components/admin/accounts/week_calendar';
import { TooltipProvider } from '@apps/web/components/ui/tooltip';
import { AdminPage } from '@apps/web/features/admin_design/admin_shell';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import {
    type AccountArrRange,
    type AccountKanbanGroup,
    ACCOUNTS_TABS,
    type AccountsTab,
    type AccountsTableSection,
    boardWithMovedCard,
    filterKanbanColumns,
    flattenKanbanColumns,
    forceMoveOfferMessage,
    groupKanbanColumnsByDri,
    groupKanbanColumnsByHealth,
    groupKanbanColumnsByKickoffWeek,
    ownerUpdateErrorMessage,
    reviewActionErrorMessage,
    selectNeedsReviewRows,
    sortAccountsTableRows,
    sortKanbanColumns,
    stageMoveErrorMessage,
} from '@apps/web/routes/_app/admin/accounts/account_board_data';
import {
    type AccountDisplaySettingsByTab,
    accountDisplaySettingsByTabAreDefault,
    accountDisplaySettingsFromSavedView,
    type AccountGroupBy,
    accountGroupByForLayout,
    type AccountOrderBy,
    type AccountOrderDirection,
    DEFAULT_ACCOUNT_DISPLAY_SETTINGS,
    DEFAULT_ACCOUNT_DISPLAY_SETTINGS_BY_TAB,
    parseStoredAccountDisplaySettings,
} from '@apps/web/routes/_app/admin/accounts/account_display_settings';
import {
    type AccountFilterChip,
    buildAccountFilterDimensions,
    parseStoredAccountFilterChips,
} from '@apps/web/routes/_app/admin/accounts/account_filters';
import { moveTableCursor } from '@apps/web/routes/_app/admin/accounts/account_keyboard_navigation';
import {
    accountFilterChipsToSearchParams,
    accountFilterSearchSchema,
    accountSearchHasFilterParams,
    searchToAccountArrRange,
    searchToAccountFilterChips,
} from '@apps/web/routes/_app/admin/accounts/account_search_params';
import {
    type AccountBoardLayout,
    type AccountBoardLayouts,
    type AccountBoardTab,
    DEFAULT_ACCOUNT_BOARD_LAYOUTS,
    getAccountSavedViewLayout,
    getEffectiveAccountSavedViewTab,
    isAccountBoardTab,
    parseStoredAccountBoardLayouts,
} from '@apps/web/routes/_app/admin/accounts/account_view_layout';
import { AccountsCalendarView } from '@apps/web/routes/_app/admin/accounts/accounts_calendar_view';
import {
    ChurnConfirmDialog,
    ForceMoveConfirmDialog,
    ReviewAccountDialog,
    type ReviewLinearTicketInput,
} from '@apps/web/routes/_app/admin/accounts/accounts_dialogs';
import {
    type AccountMoveSource,
    AccountsKanbanBoard,
    type PendingChurnMove,
} from '@apps/web/routes/_app/admin/accounts/accounts_kanban_board';
import { PageHeader } from '@apps/web/routes/_app/admin/accounts/accounts_page_header';
import { AccountsTabContent } from '@apps/web/routes/_app/admin/accounts/accounts_tab_content';
import { AccountsTable } from '@apps/web/routes/_app/admin/accounts/accounts_table_view';
import { useAccountPageHotkeys } from '@apps/web/routes/_app/admin/accounts/use_account_page_hotkeys';
import { useAccountsCalendar } from '@apps/web/routes/_app/admin/accounts/use_accounts_calendar';
import {
    createAdminAccountLinearTicket,
    reviewAdminAccount,
    updateAdminAccountKanbanStage,
    updateAdminAccountOwner,
    updateAdminAccountProvisioned,
    useAdminAccountOwnerCandidates,
    useAdminAccountSavedViews,
    useAdminAccountsKanban,
    useAdminSettings,
} from '@apps/web/swr_data_fetching/admin_accounts';
import { useUser } from '@apps/web/swr_data_fetching/current_user_queries';
import {
    type AccountKanbanCardRead,
    type AccountKanbanColumnRead,
    type AccountOwnerCandidateRead,
    type AccountSavedViewDisplay,
    type AccountSavedViewRead,
} from '@libraries/typescript/api_client';

const REFRESH_INTERVAL_MS = 30_000;
const EMPTY_COLUMNS: ReadonlyArray<AccountKanbanColumnRead> = [];
const EMPTY_OWNER_CANDIDATES: ReadonlyArray<AccountOwnerCandidateRead> = [];
const EMPTY_SAVED_VIEWS: ReadonlyArray<AccountSavedViewRead> = [];
const LEGACY_TAB_VALUES = [...ACCOUNTS_TABS, 'table'] as const;
const CALENDAR_WEEK_SEARCH_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;

const accountsSearchSchema = accountFilterSearchSchema.extend({
    tab: z.enum(LEGACY_TAB_VALUES).optional().catch('board'),
    week: z
        .string()
        .regex(CALENDAR_WEEK_SEARCH_PATTERN)
        .optional()
        .catch(undefined),
});

const STORAGE_KEY_SEARCH = 'admin-accounts-filter-search';
const STORAGE_KEY_FILTER_CHIPS = 'admin-accounts-filter-chips';
const STORAGE_KEY_SHOW_PROPOSAL_SENT =
    'admin-accounts-filter-show-proposal-sent';
const STORAGE_KEY_SHOW_CHURNED = 'admin-accounts-filter-show-churned';
const STORAGE_KEY_SHOW_TEST_ORGS = 'admin-accounts-filter-show-test-orgs';
const STORAGE_KEY_BOARD_LAYOUT = 'admin-accounts-layout';
const STORAGE_KEY_DISPLAY_SETTINGS = 'admin-accounts-display-settings';
const LEGACY_STORAGE_KEY_BOARD_SORT = 'admin-accounts-board-sort';
const LEGACY_STORAGE_KEY_GROUP_BY_DRI = 'admin-accounts-group-by-dri';
const STORAGE_KEY_COLLAPSED_DRI_LANES = 'admin-accounts-collapsed-dri-lanes';
const STORAGE_KEY_DEFAULT_VIEW = 'admin-accounts-default-view';
const STORAGE_KEY_CALENDAR_VIEW = 'admin-accounts-calendar-view';

export const Route = createFileRoute('/_app/admin/accounts/')({
    component: AdminAccountsPage,
    validateSearch: accountsSearchSchema,
    head: () => ({
        meta: [{ title: 'Accounts | Phoebe Admin' }],
    }),
});

function AdminAccountsPage() {
    const [showTestOrgs, setShowTestOrgs] = useState(() => {
        try {
            return (
                window.localStorage.getItem(STORAGE_KEY_SHOW_TEST_ORGS) ===
                'true'
            );
        } catch {
            return false;
        }
    });
    const [calendarViewMode, setCalendarViewMode] = useState<CalendarViewMode>(
        () => {
            try {
                return window.localStorage.getItem(
                    STORAGE_KEY_CALENDAR_VIEW,
                ) === 'month'
                    ? 'month'
                    : 'week';
            } catch {
                return 'week';
            }
        },
    );
    const [cohortAccountCount, setCohortAccountCount] = useState(0);
    const { data, isLoading, isValidating, error, mutate } =
        useAdminAccountsKanban({
            refreshIntervalMs: REFRESH_INTERVAL_MS,
            includeTestOrgs: showTestOrgs,
        });
    const { data: ownerCandidatesResponse } = useAdminAccountOwnerCandidates();
    const { data: savedViewsResponse, mutate: mutateSavedViews } =
        useAdminAccountSavedViews();
    const { data: adminSettings, mutate: mutateAdminSettings } =
        useAdminSettings();
    const { data: currentUser } = useUser({ reason: 'admin-accounts-board' });
    const [activeCard, setActiveCard] = useState<AccountKanbanCardRead | null>(
        null,
    );
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
        null,
    );
    const [pendingMoveAccountId, setPendingMoveAccountId] = useState<
        string | null
    >(null);
    const [pendingReviewAccountId, setPendingReviewAccountId] = useState<
        string | null
    >(null);
    const [reviewAccount, setReviewAccount] =
        useState<AccountKanbanCardRead | null>(null);
    const [reviewNote, setReviewNote] = useState('');
    const [reviewHealthSelection, setReviewHealthSelection] =
        useState<AccountHealthVerdictSelection>('no_change');
    const [reviewLinearTicketError, setReviewLinearTicketError] = useState<
        string | null
    >(null);
    const [reviewCompletedAccountId, setReviewCompletedAccountId] = useState<
        string | null
    >(null);
    const reviewAdvanceSnapshotRef = useRef<{
        readonly reviewedAccountId: string;
        readonly successorAccountId: string | null;
    } | null>(null);
    // A churn move drag is parked here until the user confirms it in the
    // churn-confirmation dialog, since it disables the org's systems.
    const [pendingChurnMove, setPendingChurnMove] =
        useState<PendingChurnMove | null>(null);
    // A move Core rejected with a forceable 409 (the owner/go-live guardrail)
    // is parked here so the user can retry it as an admin force move.
    const [pendingForceMove, setPendingForceMove] = useState<{
        readonly card: AccountKanbanCardRead;
        readonly targetStage: string;
        readonly message: string;
        readonly ownerUpdate: DriOwnerUpdate | null;
    } | null>(null);
    const [pendingProvisionAccountId, setPendingProvisionAccountId] = useState<
        string | null
    >(null);
    const [searchQuery, setSearchQuery] = useState(() => {
        try {
            return window.localStorage.getItem(STORAGE_KEY_SEARCH) ?? '';
        } catch {
            return '';
        }
    });
    const [showProposalSent, setShowProposalSent] = useState(() => {
        try {
            return (
                window.localStorage.getItem(STORAGE_KEY_SHOW_PROPOSAL_SENT) ===
                'true'
            );
        } catch {
            return false;
        }
    });
    const [showChurned, setShowChurned] = useState(() => {
        try {
            return (
                window.localStorage.getItem(STORAGE_KEY_SHOW_CHURNED) === 'true'
            );
        } catch {
            return false;
        }
    });
    const [displaySettingsByTab, setDisplaySettingsByTab] =
        useState<AccountDisplaySettingsByTab>(() => {
            try {
                return parseStoredAccountDisplaySettings(
                    window.localStorage.getItem(STORAGE_KEY_DISPLAY_SETTINGS),
                    window.localStorage.getItem(
                        LEGACY_STORAGE_KEY_GROUP_BY_DRI,
                    ),
                    window.localStorage.getItem(LEGACY_STORAGE_KEY_BOARD_SORT),
                );
            } catch {
                return DEFAULT_ACCOUNT_DISPLAY_SETTINGS_BY_TAB;
            }
        });
    const [defaultViewId, setDefaultViewId] = useState<string | null>(() => {
        try {
            return window.localStorage.getItem(STORAGE_KEY_DEFAULT_VIEW);
        } catch {
            return null;
        }
    });
    const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<
        ReadonlySet<string>
    >(() => {
        try {
            const saved = window.localStorage.getItem(
                STORAGE_KEY_COLLAPSED_DRI_LANES,
            );
            if (!saved) {
                return new Set<string>();
            }

            const storedValue: unknown = JSON.parse(saved);
            const parsed = z.array(z.string()).safeParse(storedValue);
            return parsed.success ? new Set(parsed.data) : new Set<string>();
        } catch {
            return new Set<string>();
        }
    });
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const { tab, week, view } = search;
    const activeTab: AccountsTab = tab === 'table' ? 'board' : (tab ?? 'board');
    const [boardLayouts, setBoardLayouts] = useState<AccountBoardLayouts>(
        () => {
            try {
                const storedLayouts = parseStoredAccountBoardLayouts(
                    window.localStorage.getItem(STORAGE_KEY_BOARD_LAYOUT),
                );
                return tab === 'table'
                    ? { ...storedLayouts, board: 'table' }
                    : storedLayouts;
            } catch {
                return tab === 'table'
                    ? { ...DEFAULT_ACCOUNT_BOARD_LAYOUTS, board: 'table' }
                    : DEFAULT_ACCOUNT_BOARD_LAYOUTS;
            }
        },
    );
    const boardTab: AccountBoardTab | null = isAccountBoardTab(activeTab)
        ? activeTab
        : null;
    const boardLayout = boardTab === null ? null : boardLayouts[boardTab];
    const displaySettings =
        boardTab === null
            ? DEFAULT_ACCOUNT_DISPLAY_SETTINGS
            : displaySettingsByTab[boardTab];
    const { groupBy, orderBy, orderDirection } = displaySettings;
    const isTableLayout = boardLayout === 'table';
    const filterChips = useMemo(
        () => searchToAccountFilterChips(search),
        [search],
    );
    const hasFilterSearchParams = accountSearchHasFilterParams(search);
    const arrRange = useMemo(() => searchToAccountArrRange(search), [search]);
    const onboardingWeeklyCap =
        adminSettings?.onboarding_weekly_cap ?? ONBOARDING_WEEKLY_CAP;
    const currentSavedViewDisplay = useMemo<AccountSavedViewDisplay>(
        () => ({
            ...(boardLayout === null ? {} : { layout: boardLayout }),
            ...(boardTab === null
                ? {}
                : {
                      group_by: groupBy,
                      order_by: orderBy,
                      order_direction: orderDirection,
                  }),
            show_test_orgs: showTestOrgs,
            show_proposal_sent: showProposalSent,
            show_churned: showChurned,
        }),
        [
            boardLayout,
            boardTab,
            groupBy,
            orderBy,
            orderDirection,
            showProposalSent,
            showChurned,
            showTestOrgs,
        ],
    );
    const handledViewIdRef = useRef<string | null>(null);
    const initializedUrlStateRef = useRef(false);
    const handleFilterChipsChange = useCallback(
        (nextChips: Array<AccountFilterChip>) => {
            void navigate({
                search: (current) => ({
                    ...current,
                    ...accountFilterChipsToSearchParams(nextChips),
                }),
            });
        },
        [navigate],
    );
    const handleArrRangeChange = useCallback(
        (range: AccountArrRange) => {
            void navigate({
                replace: true,
                search: (current) => ({
                    ...current,
                    arr_min:
                        range.minCents === null
                            ? undefined
                            : range.minCents / 100,
                    arr_max:
                        range.maxCents === null
                            ? undefined
                            : range.maxCents / 100,
                }),
            });
        },
        [navigate],
    );
    const handleDefaultViewChange = useCallback((viewId: string | null) => {
        setDefaultViewId(viewId);
        try {
            if (viewId === null) {
                window.localStorage.removeItem(STORAGE_KEY_DEFAULT_VIEW);
            } else {
                window.localStorage.setItem(STORAGE_KEY_DEFAULT_VIEW, viewId);
            }
        } catch {
            // The preference remains active for this session if storage fails.
        }
    }, []);
    const applySavedViewDisplay = useCallback(
        (savedView: AccountSavedViewRead) => {
            const display = savedView.payload.display;
            const effectiveTab = getEffectiveAccountSavedViewTab(
                savedView.payload.tab,
            );
            if (display !== null && display !== undefined) {
                setShowTestOrgs(display.show_test_orgs ?? false);
                setShowProposalSent(display.show_proposal_sent ?? false);
                setShowChurned(display.show_churned ?? false);
            }
            if (
                savedView.payload.tab === 'table' ||
                (display !== null && display !== undefined)
            ) {
                const savedLayout = getAccountSavedViewLayout(
                    savedView.payload.tab,
                    display,
                );
                if (isAccountBoardTab(effectiveTab) && savedLayout !== null) {
                    if (display !== null && display !== undefined) {
                        const savedSettings =
                            accountDisplaySettingsFromSavedView(display);
                        setDisplaySettingsByTab((currentSettings) => ({
                            ...currentSettings,
                            [effectiveTab]: {
                                ...savedSettings,
                                groupBy: accountGroupByForLayout(
                                    savedSettings.groupBy,
                                    savedLayout,
                                ),
                            },
                        }));
                    }
                    setBoardLayouts((currentLayouts) => ({
                        ...currentLayouts,
                        [effectiveTab]: savedLayout,
                    }));
                }
            }
        },
        [],
    );
    const handleApplySavedView = useCallback(
        (savedView: AccountSavedViewRead) => {
            handledViewIdRef.current = savedView.id;
            applySavedViewDisplay(savedView);
            const effectiveTab = getEffectiveAccountSavedViewTab(
                savedView.payload.tab,
            );

            const chips = savedView.payload.filters.map((filter) => ({
                dimension: filter.dimension,
                operator: filter.operator ?? 'is',
                values: [...filter.values],
            }));
            void navigate({
                search: (current) => ({
                    ...current,
                    ...accountFilterChipsToSearchParams(chips),
                    tab: effectiveTab === 'board' ? undefined : effectiveTab,
                    view: savedView.id,
                }),
            });
        },
        [applySavedViewDisplay, navigate],
    );
    const handleClearSavedView = useCallback(() => {
        handledViewIdRef.current = null;
        void navigate({
            search: (current) => ({
                ...current,
                ...accountFilterChipsToSearchParams([]),
                view: undefined,
            }),
        });
    }, [navigate]);
    const handleActiveTabChange = useCallback(
        (value: AccountsTab) => {
            void navigate({
                search: (current) => ({
                    ...current,
                    tab: value === 'board' ? undefined : value,
                }),
            });
        },
        [navigate],
    );
    const handleBoardLayoutChange = useCallback(
        (layout: AccountBoardLayout) => {
            if (boardTab === null) {
                return;
            }
            setBoardLayouts((currentLayouts) =>
                currentLayouts[boardTab] === layout
                    ? currentLayouts
                    : { ...currentLayouts, [boardTab]: layout },
            );
            if (layout === 'board') {
                setDisplaySettingsByTab((currentSettings) => ({
                    ...currentSettings,
                    [boardTab]: {
                        ...currentSettings[boardTab],
                        groupBy: accountGroupByForLayout(
                            currentSettings[boardTab].groupBy,
                            layout,
                        ),
                    },
                }));
            }
        },
        [boardTab],
    );
    const handleGroupByChange = useCallback(
        (value: AccountGroupBy) => {
            if (boardTab === null) {
                return;
            }
            setDisplaySettingsByTab((currentSettings) => ({
                ...currentSettings,
                [boardTab]: {
                    ...currentSettings[boardTab],
                    groupBy: value,
                },
            }));
        },
        [boardTab],
    );
    const handleOrderByChange = useCallback(
        (value: AccountOrderBy) => {
            if (boardTab === null) {
                return;
            }
            setDisplaySettingsByTab((currentSettings) => ({
                ...currentSettings,
                [boardTab]: {
                    ...currentSettings[boardTab],
                    orderBy: value,
                },
            }));
        },
        [boardTab],
    );
    const handleOrderDirectionChange = useCallback(
        (value: AccountOrderDirection) => {
            if (boardTab === null) {
                return;
            }
            setDisplaySettingsByTab((currentSettings) => ({
                ...currentSettings,
                [boardTab]: {
                    ...currentSettings[boardTab],
                    orderDirection: value,
                },
            }));
        },
        [boardTab],
    );
    const navigateToCalendarWeek = useCallback(
        (weekStartKey: string) => {
            void navigate({
                search: (current) => ({
                    ...current,
                    tab: 'calendar',
                    week: weekStartKey,
                }),
            });
        },
        [navigate],
    );

    useEffect(() => {
        if (tab !== 'table') {
            return;
        }

        setBoardLayouts((currentLayouts) =>
            currentLayouts.board === 'table'
                ? currentLayouts
                : { ...currentLayouts, board: 'table' },
        );
        void navigate({
            replace: true,
            search: (current) => ({ ...current, tab: undefined }),
        });
    }, [navigate, tab]);

    useEffect(() => {
        if (view === undefined) {
            handledViewIdRef.current = null;
            return;
        }
        if (
            handledViewIdRef.current === view ||
            savedViewsResponse === undefined
        ) {
            return;
        }

        const savedView = savedViewsResponse.views.find(
            (candidate) => candidate.id === view,
        );
        if (!savedView) {
            return;
        }
        handledViewIdRef.current = view;
        if (hasFilterSearchParams) {
            applySavedViewDisplay(savedView);
            const effectiveTab = getEffectiveAccountSavedViewTab(
                savedView.payload.tab,
            );
            void navigate({
                replace: true,
                search: (current) => ({
                    ...current,
                    tab: effectiveTab === 'board' ? undefined : effectiveTab,
                }),
            });
            return;
        }
        handleApplySavedView(savedView);
    }, [
        applySavedViewDisplay,
        handleApplySavedView,
        hasFilterSearchParams,
        navigate,
        savedViewsResponse,
        view,
    ]);

    useEffect(() => {
        if (initializedUrlStateRef.current) {
            return;
        }

        let storedFilterChips: Array<AccountFilterChip> = [];
        try {
            storedFilterChips = parseStoredAccountFilterChips(
                window.localStorage.getItem(STORAGE_KEY_FILTER_CHIPS),
            );
            window.localStorage.removeItem(STORAGE_KEY_FILTER_CHIPS);
        } catch {
            // Continue to the default view when storage is unavailable.
        }
        if (
            view === undefined &&
            !hasFilterSearchParams &&
            storedFilterChips.length > 0
        ) {
            initializedUrlStateRef.current = true;
            handleFilterChipsChange(storedFilterChips);
            return;
        }

        const hasExplicitUrlState =
            tab !== undefined || view !== undefined || hasFilterSearchParams;
        if (hasExplicitUrlState) {
            initializedUrlStateRef.current = true;
            return;
        }

        if (savedViewsResponse === undefined) {
            return;
        }

        initializedUrlStateRef.current = true;
        const personalDefaultView =
            defaultViewId === null
                ? undefined
                : savedViewsResponse.views.find(
                      (candidate) => candidate.id === defaultViewId,
                  );
        // Core only allows shared team defaults, but stale data could still
        // carry a personal one — never apply a view the admin cannot see in
        // the pill row.
        const defaultView =
            personalDefaultView ??
            savedViewsResponse.views.find(
                (candidate) =>
                    candidate.is_team_default && candidate.scope === 'shared',
            );
        if (defaultView) {
            handleApplySavedView(defaultView);
        }
    }, [
        defaultViewId,
        handleApplySavedView,
        handleFilterChipsChange,
        hasFilterSearchParams,
        savedViewsResponse,
        tab,
        view,
    ]);

    useEffect(() => {
        try {
            window.localStorage.setItem(
                STORAGE_KEY_CALENDAR_VIEW,
                calendarViewMode,
            );
        } catch {
            // Ignore storage errors (private browsing, quota exceeded, etc.)
        }
    }, [calendarViewMode]);

    useEffect(() => {
        try {
            if (searchQuery) {
                window.localStorage.setItem(STORAGE_KEY_SEARCH, searchQuery);
            } else {
                window.localStorage.removeItem(STORAGE_KEY_SEARCH);
            }
        } catch {
            // Ignore storage errors (private browsing, quota exceeded, etc.)
        }
    }, [searchQuery]);

    useEffect(() => {
        try {
            if (showProposalSent) {
                window.localStorage.setItem(
                    STORAGE_KEY_SHOW_PROPOSAL_SENT,
                    'true',
                );
            } else {
                window.localStorage.removeItem(STORAGE_KEY_SHOW_PROPOSAL_SENT);
            }
        } catch {
            // Ignore storage errors (private browsing, quota exceeded, etc.)
        }
    }, [showProposalSent]);

    useEffect(() => {
        try {
            if (showChurned) {
                window.localStorage.setItem(STORAGE_KEY_SHOW_CHURNED, 'true');
            } else {
                window.localStorage.removeItem(STORAGE_KEY_SHOW_CHURNED);
            }
        } catch {
            // Ignore storage errors (private browsing, quota exceeded, etc.)
        }
    }, [showChurned]);

    useEffect(() => {
        try {
            if (showTestOrgs) {
                window.localStorage.setItem(STORAGE_KEY_SHOW_TEST_ORGS, 'true');
            } else {
                window.localStorage.removeItem(STORAGE_KEY_SHOW_TEST_ORGS);
            }
        } catch {
            // Ignore storage errors (private browsing, quota exceeded, etc.)
        }
    }, [showTestOrgs]);

    useEffect(() => {
        try {
            if (accountDisplaySettingsByTabAreDefault(displaySettingsByTab)) {
                window.localStorage.removeItem(STORAGE_KEY_DISPLAY_SETTINGS);
            } else {
                window.localStorage.setItem(
                    STORAGE_KEY_DISPLAY_SETTINGS,
                    JSON.stringify(displaySettingsByTab),
                );
            }
        } catch {
            // Ignore storage errors (private browsing, quota exceeded, etc.)
        }
    }, [displaySettingsByTab]);

    useEffect(() => {
        try {
            if (
                boardLayouts.board === DEFAULT_ACCOUNT_BOARD_LAYOUTS.board &&
                boardLayouts.account_management ===
                    DEFAULT_ACCOUNT_BOARD_LAYOUTS.account_management
            ) {
                window.localStorage.removeItem(STORAGE_KEY_BOARD_LAYOUT);
            } else {
                window.localStorage.setItem(
                    STORAGE_KEY_BOARD_LAYOUT,
                    JSON.stringify(boardLayouts),
                );
            }
        } catch {
            // Ignore storage errors (private browsing, quota exceeded, etc.)
        }
    }, [boardLayouts]);

    useEffect(() => {
        try {
            const storedLaneKeys = [...collapsedGroupKeys].sort();
            if (storedLaneKeys.length > 0) {
                window.localStorage.setItem(
                    STORAGE_KEY_COLLAPSED_DRI_LANES,
                    JSON.stringify(storedLaneKeys),
                );
            } else {
                window.localStorage.removeItem(STORAGE_KEY_COLLAPSED_DRI_LANES);
            }
        } catch {
            // Ignore storage errors (private browsing, quota exceeded, etc.)
        }
    }, [collapsedGroupKeys]);

    const handleRefresh = useCallback(() => {
        void mutate();
    }, [mutate]);

    const handleGroupCollapsedChange = useCallback(
        (groupKey: string, collapsed: boolean) => {
            setCollapsedGroupKeys((currentGroupKeys) => {
                const nextGroupKeys = new Set(currentGroupKeys);
                if (collapsed) {
                    nextGroupKeys.add(groupKey);
                } else {
                    nextGroupKeys.delete(groupKey);
                }
                return nextGroupKeys;
            });
        },
        [],
    );

    const columns = data?.columns ?? EMPTY_COLUMNS;
    const ownerCandidates =
        ownerCandidatesResponse?.owners ?? EMPTY_OWNER_CANDIDATES;

    const filterDimensions = useMemo(
        () => buildAccountFilterDimensions(columns, ownerCandidates),
        [columns, ownerCandidates],
    );
    const showChurnedDropTarget =
        boardTab === 'account_management' &&
        activeCard !== null &&
        !showChurned;
    const visibleColumns = useMemo(
        () =>
            sortKanbanColumns(
                filterKanbanColumns(columns, {
                    boardTab,
                    searchQuery,
                    filterChips,
                    showProposalSent,
                    showChurned,
                    showChurnedDropTarget,
                    allAccounts: activeTab === 'all',
                    arrRange,
                }),
                orderBy,
                orderDirection,
            ),
        [
            activeTab,
            arrRange,
            boardTab,
            columns,
            searchQuery,
            filterChips,
            orderBy,
            orderDirection,
            showProposalSent,
            showChurned,
            showChurnedDropTarget,
        ],
    );
    const calendar = useAccountsCalendar({
        week,
        activeTab,
        viewMode: calendarViewMode,
        onViewModeChange: setCalendarViewMode,
        visibleColumns,
        onboardingWeeklyCap,
        navigateToWeek: navigateToCalendarWeek,
    });
    const ownerGroups = useMemo(
        () => groupKanbanColumnsByDri(visibleColumns, ownerCandidates),
        [ownerCandidates, visibleColumns],
    );
    const healthGroups = useMemo(
        () => groupKanbanColumnsByHealth(visibleColumns),
        [visibleColumns],
    );
    const kickoffWeekGroups = useMemo(
        () => groupKanbanColumnsByKickoffWeek(visibleColumns),
        [visibleColumns],
    );
    const boardGroups = useMemo<ReadonlyArray<AccountKanbanGroup>>(() => {
        switch (groupBy) {
            case 'owner':
                return ownerGroups;
            case 'health':
                return healthGroups;
            case 'kickoff_week':
                return kickoffWeekGroups;
            case 'none':
            case 'stage':
                return [];
            default:
                return groupBy satisfies never;
        }
    }, [groupBy, healthGroups, kickoffWeekGroups, ownerGroups]);
    const tableRows = useMemo(
        () => flattenKanbanColumns(visibleColumns),
        [visibleColumns],
    );
    const sortedTableRows = useMemo(
        () => sortAccountsTableRows(tableRows, orderBy, orderDirection),
        [orderBy, orderDirection, tableRows],
    );
    // The Needs Review tab is the per-user review queue: accounts whose review
    // is due, scoped to the signed-in admin by default (see
    // selectNeedsReviewRows).
    const needsReviewRows = useMemo(
        () =>
            selectNeedsReviewRows(
                sortedTableRows,
                filterChips,
                currentUser?.email ?? null,
            ),
        [sortedTableRows, filterChips, currentUser],
    );
    const totalCards =
        activeTab === 'cohorts'
            ? cohortAccountCount
            : activeTab === 'needs_review'
              ? needsReviewRows.length
              : visibleColumns.reduce(
                    (total, column) => total + column.cards.length,
                    0,
                );
    const groupedTableSections =
        useMemo((): ReadonlyArray<AccountsTableSection> | null => {
            let groups: ReadonlyArray<AccountKanbanGroup>;
            switch (groupBy) {
                case 'owner':
                    groups = ownerGroups;
                    break;
                case 'health':
                    groups = healthGroups;
                    break;
                case 'stage':
                    if (visibleColumns.length <= 1) {
                        return null;
                    }
                    groups = visibleColumns
                        .filter((column) => column.cards.length > 0)
                        .map((column) => ({
                            kind: 'stage',
                            key: `stage:${column.stage}`,
                            name: column.title,
                            cardCount: column.cards.length,
                            columns: [column],
                        }));
                    break;
                case 'kickoff_week':
                    groups = kickoffWeekGroups;
                    break;
                case 'none':
                    return null;
                default:
                    return groupBy satisfies never;
            }

            return groups.map((group) => ({
                group,
                rows: sortAccountsTableRows(
                    flattenKanbanColumns(group.columns),
                    orderBy,
                    orderDirection,
                ),
            }));
        }, [
            groupBy,
            healthGroups,
            kickoffWeekGroups,
            orderBy,
            orderDirection,
            ownerGroups,
            visibleColumns,
        ]);
    const tableKeyboardAccountIds = useMemo(() => {
        if (groupedTableSections === null) {
            return sortedTableRows.map((row) => row.card.id);
        }

        return groupedTableSections
            .filter(({ group }) => !collapsedGroupKeys.has(group.key))
            .flatMap(({ rows }) => rows.map((row) => row.card.id));
    }, [collapsedGroupKeys, groupedTableSections, sortedTableRows]);
    // All Accounts lists every stage as its own section, in pipeline order.
    const allAccountsSections = useMemo(
        (): ReadonlyArray<AccountsTableSection> =>
            activeTab !== 'all'
                ? []
                : visibleColumns
                      .filter((column) => column.cards.length > 0)
                      .map((column) => ({
                          group: {
                              kind: 'stage',
                              key: `stage:${column.stage}`,
                              name: column.title,
                              cardCount: column.cards.length,
                              columns: [column],
                          },
                          rows: sortAccountsTableRows(
                              flattenKanbanColumns([column]),
                              'name',
                              'asc',
                          ),
                      })),
        [activeTab, visibleColumns],
    );
    const allAccountsKeyboardAccountIds = useMemo(
        () =>
            allAccountsSections
                .filter(({ group }) => !collapsedGroupKeys.has(group.key))
                .flatMap(({ rows }) => rows.map((row) => row.card.id)),
        [allAccountsSections, collapsedGroupKeys],
    );
    const needsReviewKeyboardAccountIds = useMemo(
        () => needsReviewRows.map((row) => row.card.id),
        [needsReviewRows],
    );
    const boardKeyboardColumns = useMemo(() => {
        if (boardGroups.length === 0) {
            return visibleColumns.map((column) => ({
                laneKey: null,
                accountIds: column.cards.map((card) => card.id),
            }));
        }

        return boardGroups
            .filter((group) => !collapsedGroupKeys.has(group.key))
            .flatMap((group) =>
                group.columns.map((column) => ({
                    laneKey: group.key,
                    accountIds: column.cards.map((card) => card.id),
                })),
            );
    }, [boardGroups, collapsedGroupKeys, visibleColumns]);
    const visibleKeyboardAccountIds = useMemo(() => {
        if (isTableLayout) {
            return tableKeyboardAccountIds;
        }
        if (activeTab === 'needs_review') {
            return needsReviewKeyboardAccountIds;
        }
        if (activeTab === 'all') {
            return allAccountsKeyboardAccountIds;
        }
        if (boardTab !== null) {
            return boardKeyboardColumns.flatMap((column) => column.accountIds);
        }
        return [];
    }, [
        activeTab,
        allAccountsKeyboardAccountIds,
        boardKeyboardColumns,
        boardTab,
        isTableLayout,
        needsReviewKeyboardAccountIds,
        tableKeyboardAccountIds,
    ]);
    const selectedAccount = useMemo(
        () =>
            selectedAccountId === null
                ? null
                : (visibleColumns
                      .flatMap((column) => column.cards)
                      .find((card) => card.id === selectedAccountId) ?? null),
        [selectedAccountId, visibleColumns],
    );
    const fetchedAt = data?.fetched_at ? new Date(data.fetched_at) : null;

    useEffect(() => {
        setSelectedAccountId((currentAccountId) =>
            currentAccountId !== null &&
            !visibleKeyboardAccountIds.includes(currentAccountId)
                ? null
                : currentAccountId,
        );
    }, [visibleKeyboardAccountIds]);

    useEffect(() => {
        if (selectedAccountId === null) {
            return;
        }
        const selectedElement = document.querySelector<HTMLElement>(
            `[data-account-cursor-id="${selectedAccountId}"]`,
        );
        selectedElement?.scrollIntoView({ block: 'nearest' });
    }, [activeTab, selectedAccountId, visibleKeyboardAccountIds]);

    const moveCard = useCallback(
        async (
            card: AccountKanbanCardRead,
            targetStage: string,
            source: AccountMoveSource,
            ownerUpdate: DriOwnerUpdate | null = null,
        ) => {
            const stageChanged = targetStage !== card.kanban_stage;
            if (
                !data ||
                pendingMoveAccountId ||
                (!stageChanged && ownerUpdate === null)
            ) {
                return;
            }

            const previousBoard = data;
            const optimisticBoard = boardWithMovedCard(
                previousBoard,
                card.id,
                targetStage,
                source,
                ownerUpdate,
            );
            setPendingMoveAccountId(card.id);
            await mutate(optimisticBoard, { revalidate: false });

            let failedOperation: 'owner' | 'stage' = stageChanged
                ? 'stage'
                : 'owner';
            try {
                if (stageChanged) {
                    await updateAdminAccountKanbanStage(card.id, {
                        stage: targetStage,
                        source,
                    });
                }
                if (ownerUpdate) {
                    // Commit a combined move's stage first so a failed or
                    // cancelled force move cannot persist owner side effects.
                    failedOperation = 'owner';
                    await updateAdminAccountOwner(card.id, {
                        email: ownerUpdate.email,
                    });
                }
                const successMessage = stageChanged
                    ? ownerUpdate
                        ? 'Account stage and owner updated'
                        : 'Account stage updated'
                    : ownerUpdate?.email
                      ? 'Account owner updated'
                      : 'Account owner cleared';
                toast.success(successMessage, {
                    persistNotification: false,
                });
                await mutate();
            } catch (updateError) {
                await mutate(previousBoard, { revalidate: false });
                // Core rejects guarded moves (e.g. missing owner or go-live
                // date) with a forceable 409; offer the admin force-move
                // retry instead of a dead-end toast.
                const forceOfferMessage =
                    failedOperation !== 'stage' || source === 'force_move'
                        ? null
                        : forceMoveOfferMessage(updateError);
                if (forceOfferMessage) {
                    setPendingForceMove({
                        card,
                        targetStage,
                        message: forceOfferMessage,
                        ownerUpdate,
                    });
                } else {
                    toast.error(
                        failedOperation === 'stage'
                            ? stageMoveErrorMessage(updateError)
                            : ownerUpdateErrorMessage(updateError),
                        { persistNotification: false },
                    );
                }
                await mutate();
            } finally {
                setPendingMoveAccountId(null);
            }
        },
        [data, mutate, pendingMoveAccountId],
    );
    const handleOpenReview = useCallback(
        (account: AccountKanbanCardRead) => {
            // A review refresh can re-sort the row before a Linear retry, so
            // preserve the successor from the order the admin acted on.
            reviewAdvanceSnapshotRef.current = {
                reviewedAccountId: account.id,
                successorAccountId: moveTableCursor(
                    visibleKeyboardAccountIds,
                    account.id,
                    'next',
                ),
            };
            setReviewAccount(account);
            setReviewNote('');
            setReviewHealthSelection('no_change');
            setReviewLinearTicketError(null);
            setReviewCompletedAccountId(null);
        },
        [visibleKeyboardAccountIds],
    );
    const handleSubmitReview = useCallback(
        async (linearTicket: ReviewLinearTicketInput | null) => {
            const entry = reviewNote.trim();
            if (!reviewAccount || !entry || pendingReviewAccountId) {
                return;
            }

            setPendingReviewAccountId(reviewAccount.id);
            setReviewLinearTicketError(null);
            try {
                if (reviewCompletedAccountId !== reviewAccount.id) {
                    if (
                        reviewAdvanceSnapshotRef.current?.reviewedAccountId !==
                        reviewAccount.id
                    ) {
                        reviewAdvanceSnapshotRef.current = {
                            reviewedAccountId: reviewAccount.id,
                            successorAccountId: moveTableCursor(
                                visibleKeyboardAccountIds,
                                reviewAccount.id,
                                'next',
                            ),
                        };
                    }
                    await reviewAdminAccount(reviewAccount.id, {
                        entry,
                        ...(reviewHealthSelection === 'no_change'
                            ? {}
                            : { healthStatus: reviewHealthSelection }),
                    });
                    setReviewCompletedAccountId(reviewAccount.id);
                }
                if (linearTicket) {
                    try {
                        const response = await createAdminAccountLinearTicket(
                            reviewAccount.id,
                            linearTicket,
                        );
                        toast.success(
                            `Linear ticket ${response.ticket.issue_identifier} created`,
                            {
                                persistNotification: false,
                            },
                        );
                    } catch {
                        // Keep the dialog and draft open so the admin can
                        // retry the ticket without submitting the review again.
                        setReviewLinearTicketError(
                            'Failed to create Linear ticket. Check the details and try again.',
                        );
                        toast.error('Failed to create Linear ticket', {
                            persistNotification: false,
                        });
                        await mutate();
                        return;
                    }
                }
                toast.success('Account reviewed', {
                    persistNotification: false,
                });
                const reviewAdvanceSnapshot = reviewAdvanceSnapshotRef.current;
                const snapshottedSuccessorAccountId =
                    reviewAdvanceSnapshot?.reviewedAccountId ===
                    reviewAccount.id
                        ? reviewAdvanceSnapshot.successorAccountId
                        : null;
                const nextSelectedAccountId =
                    snapshottedSuccessorAccountId !== null &&
                    visibleKeyboardAccountIds.includes(
                        snapshottedSuccessorAccountId,
                    )
                        ? snapshottedSuccessorAccountId
                        : moveTableCursor(
                              visibleKeyboardAccountIds,
                              reviewAccount.id,
                              'next',
                          );
                reviewAdvanceSnapshotRef.current = null;
                setReviewAccount(null);
                setReviewNote('');
                setReviewHealthSelection('no_change');
                setReviewLinearTicketError(null);
                setReviewCompletedAccountId(null);
                setSelectedAccountId(nextSelectedAccountId);
                await mutate();
            } catch (error) {
                toast.error(reviewActionErrorMessage(error), {
                    persistNotification: false,
                });
            } finally {
                setPendingReviewAccountId(null);
            }
        },
        [
            mutate,
            pendingReviewAccountId,
            reviewCompletedAccountId,
            reviewAccount,
            reviewHealthSelection,
            reviewNote,
            visibleKeyboardAccountIds,
        ],
    );
    const toggleProvisioned = useCallback(
        async (card: AccountKanbanCardRead) => {
            if (pendingProvisionAccountId) {
                return;
            }

            const provisioned = !card.manually_provisioned_at;
            setPendingProvisionAccountId(card.id);
            try {
                await updateAdminAccountProvisioned(card.id, { provisioned });
                toast.success(
                    provisioned
                        ? 'Marked provisioned'
                        : 'Provisioned mark cleared',
                    {
                        persistNotification: false,
                    },
                );
            } catch (provisionError) {
                toast.error(
                    provisionError instanceof Error
                        ? provisionError.message
                        : 'Provisioned mark did not save.',
                    { persistNotification: false },
                );
            } finally {
                setPendingProvisionAccountId(null);
                // Revalidate either way: with Briefed also set, Core
                // auto-moves the card to Kickoff Call.
                await mutate();
            }
        },
        [mutate, pendingProvisionAccountId],
    );

    const handleOwnerQuickAssign = useCallback(
        (card: AccountKanbanCardRead, ownerUpdate: DriOwnerUpdate) => {
            if (
                pendingMoveAccountId !== null &&
                pendingMoveAccountId !== card.id
            ) {
                toast.error('Another account update is still in progress', {
                    persistNotification: false,
                });
                return;
            }

            const isClearingOwner = ownerUpdate.email === null;
            if (
                (isClearingOwner && !card.owner_email && !card.owner_name) ||
                (!isClearingOwner &&
                    ownerUpdate.email === (card.owner_email ?? null))
            ) {
                return;
            }
            void moveCard(card, card.kanban_stage, 'drag_drop', ownerUpdate);
        },
        [moveCard, pendingMoveAccountId],
    );

    const accountDialogOpen =
        reviewAccount !== null ||
        pendingChurnMove !== null ||
        pendingForceMove !== null;
    // Explicit state blocks page-owned overlays and active drags immediately;
    // useAccountPageHotkey also catches child-owned Radix overlays in the DOM.
    const pageHotkeysEnabled = !accountDialogOpen && activeCard === null;

    const handleOpenAccount = useCallback(
        (accountId: string) => {
            void navigate({
                to: '/admin/accounts/$accountId',
                params: { accountId },
            });
        },
        [navigate],
    );
    useAccountPageHotkeys({
        activeTab,
        boardTab,
        isTableLayout,
        isLoading,
        error,
        hotkeysEnabled: pageHotkeysEnabled,
        visibleKeyboardAccountIds,
        tableKeyboardAccountIds,
        boardKeyboardColumns,
        selectedAccount,
        selectedAccountId,
        setSelectedAccountId,
        onOpenAccount: handleOpenAccount,
        onReviewAccount: handleOpenReview,
        onActiveTabChange: handleActiveTabChange,
        onCalendarArrowNavigation: calendar.handleArrowNavigation,
    });

    return (
        <TooltipProvider delayDuration={150}>
            <AdminPage className="h-svh max-h-svh flex-1 overflow-hidden">
                <PageHeader
                    totalCards={totalCards}
                    fetchedAt={fetchedAt}
                    isValidating={isValidating}
                    onRefresh={handleRefresh}
                    adminSettings={adminSettings}
                    onAdminSettingsRefresh={mutateAdminSettings}
                    searchQuery={searchQuery}
                    onSearchQueryChange={setSearchQuery}
                    filterDimensions={filterDimensions}
                    filterChips={filterChips}
                    onFilterChipsChange={handleFilterChipsChange}
                    arrRange={arrRange}
                    onArrRangeChange={handleArrRangeChange}
                    savedViews={savedViewsResponse?.views ?? EMPTY_SAVED_VIEWS}
                    onSavedViewsRefresh={mutateSavedViews}
                    activeSavedViewId={view ?? null}
                    currentSavedViewDisplay={currentSavedViewDisplay}
                    defaultViewId={defaultViewId}
                    onApplySavedView={handleApplySavedView}
                    onClearSavedView={handleClearSavedView}
                    onDefaultViewChange={handleDefaultViewChange}
                    ownerCandidates={ownerCandidates}
                    currentUserEmail={currentUser?.email ?? null}
                    boardLayout={boardLayout}
                    onBoardLayoutChange={handleBoardLayoutChange}
                    showProposalSent={showProposalSent}
                    onShowProposalSentChange={setShowProposalSent}
                    showChurned={showChurned}
                    onShowChurnedChange={setShowChurned}
                    showTestOrgs={showTestOrgs}
                    onShowTestOrgsChange={setShowTestOrgs}
                    groupBy={groupBy}
                    onGroupByChange={handleGroupByChange}
                    orderBy={orderBy}
                    onOrderByChange={handleOrderByChange}
                    orderDirection={orderDirection}
                    onOrderDirectionChange={handleOrderDirectionChange}
                    activeTab={activeTab}
                    onActiveTabChange={handleActiveTabChange}
                    hotkeysEnabled={pageHotkeysEnabled}
                />
                <div
                    className={cn(
                        'flex min-h-0 flex-1 flex-col overflow-y-auto',
                        activeTab === 'board' ||
                            activeTab === 'account_management'
                            ? 'gap-3 px-4 py-3'
                            : 'gap-4 p-6',
                    )}
                >
                    <AccountsTabContent
                        activeTab={activeTab}
                        error={error}
                        isLoading={isLoading}
                        isTableLayout={isTableLayout}
                        showTestOrgs={showTestOrgs}
                        onCohortAccountCountChange={setCohortAccountCount}
                        needsReviewTable={
                            <AccountsTable
                                rows={needsReviewRows}
                                sections={null}
                                collapsedGroupKeys={collapsedGroupKeys}
                                selectedAccountId={selectedAccountId}
                                pendingReviewAccountId={pendingReviewAccountId}
                                onGroupCollapsedChange={
                                    handleGroupCollapsedChange
                                }
                                onMarkReviewed={handleOpenReview}
                                ownerCandidates={ownerCandidates}
                                onOwnerQuickAssign={handleOwnerQuickAssign}
                                emptyMessage="No accounts are due for review. You’re all caught up."
                            />
                        }
                        allAccountsTable={
                            <AccountsTable
                                rows={sortedTableRows}
                                sections={allAccountsSections}
                                collapsedGroupKeys={collapsedGroupKeys}
                                selectedAccountId={selectedAccountId}
                                pendingReviewAccountId={pendingReviewAccountId}
                                onGroupCollapsedChange={
                                    handleGroupCollapsedChange
                                }
                                onMarkReviewed={handleOpenReview}
                                ownerCandidates={ownerCandidates}
                                onOwnerQuickAssign={handleOwnerQuickAssign}
                            />
                        }
                        calendar={
                            <AccountsCalendarView
                                calendar={calendar}
                                ownerCandidates={ownerCandidates}
                            />
                        }
                        table={
                            <AccountsTable
                                rows={sortedTableRows}
                                sections={groupedTableSections}
                                collapsedGroupKeys={collapsedGroupKeys}
                                selectedAccountId={selectedAccountId}
                                pendingReviewAccountId={pendingReviewAccountId}
                                onGroupCollapsedChange={
                                    handleGroupCollapsedChange
                                }
                                onMarkReviewed={handleOpenReview}
                                ownerCandidates={ownerCandidates}
                                onOwnerQuickAssign={handleOwnerQuickAssign}
                            />
                        }
                        board={
                            <AccountsKanbanBoard
                                columns={columns}
                                visibleColumns={visibleColumns}
                                boardGroups={boardGroups}
                                ownerGroups={ownerGroups}
                                groupBy={groupBy}
                                boardTab={boardTab}
                                activeCard={activeCard}
                                onActiveCardChange={setActiveCard}
                                collapsedGroupKeys={collapsedGroupKeys}
                                onGroupCollapsedChange={
                                    handleGroupCollapsedChange
                                }
                                selectedAccountId={selectedAccountId}
                                pendingMoveAccountId={pendingMoveAccountId}
                                pendingProvisionAccountId={
                                    pendingProvisionAccountId
                                }
                                onToggleProvisioned={toggleProvisioned}
                                ownerCandidates={ownerCandidates}
                                onOwnerQuickAssign={handleOwnerQuickAssign}
                                showChurnedDropTarget={showChurnedDropTarget}
                                onMoveCard={moveCard}
                                onChurnMoveRequest={setPendingChurnMove}
                            />
                        }
                    />
                </div>
            </AdminPage>
            <ChurnConfirmDialog
                pendingChurnMove={pendingChurnMove}
                isMoving={pendingMoveAccountId !== null}
                onCancel={() => setPendingChurnMove(null)}
                onConfirm={() => {
                    if (!pendingChurnMove) {
                        return;
                    }
                    const { card, targetStage, source, ownerUpdate } =
                        pendingChurnMove;
                    setPendingChurnMove(null);
                    void moveCard(card, targetStage, source, ownerUpdate);
                }}
            />
            <ForceMoveConfirmDialog
                pendingForceMove={pendingForceMove}
                isMoving={pendingMoveAccountId !== null}
                onCancel={() => setPendingForceMove(null)}
                onConfirm={() => {
                    if (!pendingForceMove) {
                        return;
                    }
                    const { card, targetStage, ownerUpdate } = pendingForceMove;
                    setPendingForceMove(null);
                    void moveCard(card, targetStage, 'force_move', ownerUpdate);
                }}
            />
            <ReviewAccountDialog
                account={reviewAccount}
                note={reviewNote}
                healthSelection={reviewHealthSelection}
                linearTicketError={reviewLinearTicketError}
                isReviewCompleted={
                    reviewCompletedAccountId === reviewAccount?.id
                }
                isSubmitting={pendingReviewAccountId !== null}
                onNoteChange={setReviewNote}
                onHealthSelectionChange={setReviewHealthSelection}
                onCancel={() => {
                    reviewAdvanceSnapshotRef.current = null;
                    setReviewAccount(null);
                    setReviewNote('');
                    setReviewHealthSelection('no_change');
                    setReviewLinearTicketError(null);
                    setReviewCompletedAccountId(null);
                }}
                onSubmit={(linearTicket) =>
                    void handleSubmitReview(linearTicket)
                }
            />
        </TooltipProvider>
    );
}
